import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, ElementRef, ViewChild } from '@angular/core';
import * as L from 'leaflet';

export interface RoutePoint {
  lat: number;
  lon: number;
  name: string;
}

export interface VehicleRoute {
  id: string;
  name: string;
  identifier: string;
  type: string;
  fromPoint: RoutePoint;
  toPoint: RoutePoint;
  distance?: number | null; // Distance in kilometers (can be null if not calculated)
  coordinates?: L.LatLng[]; // Detailed route coordinates for drawing the actual path
  color?: string;
}

@Component({
    selector: 'app-route-map',
    templateUrl: './route-map.component.html',
    styleUrls: ['./route-map.component.scss'],
    standalone: false
})
export class RouteMapComponent implements OnInit, OnDestroy, OnChanges {
  @Input() fromPoint: RoutePoint | null = null;
  @Input() toPoint: RoutePoint | null = null;
  @Input() vehicleRoutes: VehicleRoute[] = [];
  @Input() mapHeight: string = '320px';
  @Input() mapId: string = 'routeMap';
  @Input() showMainRoute: boolean = true; // Controls whether to show main transport route
  @Input() interactive: boolean = true; // Controls map interactions (zoom, drag, etc.)

  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef;

  private map: L.Map | null = null;
  private initialBounds: L.LatLngBounds | null = null;
  private resetViewControl: L.Control | null = null; // Track reset view control
  private vehicleColors: string[] = [
    '#3b82f6', // blue
    '#ef4444', // red
    '#10b981', // green
    '#f59e0b', // yellow
    '#8b5cf6', // purple
    '#f97316', // orange
    '#06b6d4', // cyan
    '#84cc16', // lime
    '#ec4899', // pink
    '#6b7280'  // gray
  ];

  ngOnInit() {
    if (this.shouldShowMap()) {
      setTimeout(() => this.initializeMap(), 0);
    }
  }

  ngOnDestroy() {
    this.destroyMap();
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes['fromPoint'] || changes['toPoint'] || changes['vehicleRoutes']) && this.map) {
      if (this.shouldShowMap()) {
        this.updateMap();
      } else {
        this.destroyMap();
      }
    } else if ((changes['interactive']) && this.map) {
      // Recreate map when interactive setting changes
      this.destroyMap();
      if (this.shouldShowMap()) {
        setTimeout(() => this.initializeMap(), 0);
      }
    } else if (this.shouldShowMap() && !this.map) {
      setTimeout(() => this.initializeMap(), 0);
    }
  }

  shouldShowMap(): boolean {
    // Show map if we have main route OR vehicle routes
    const hasMainRoute = !!(this.fromPoint?.lat && this.fromPoint?.lon && 
                           this.toPoint?.lat && this.toPoint?.lon);
    const hasVehicleRoutes = this.vehicleRoutes && this.vehicleRoutes.length > 0;
    
    return hasMainRoute || hasVehicleRoutes;
  }

  shouldShowNoDataMessage(): boolean {
    // Show "no data" message if we have main route (from/to points) but no vehicles
    // BUT only if vehicleRoutes is not explicitly set to empty array (which indicates add-dialog mode)
    const hasMainRoute = !!(this.fromPoint?.lat && this.fromPoint?.lon && 
                           this.toPoint?.lat && this.toPoint?.lon);
    const hasVehicleRoutes = this.vehicleRoutes && this.vehicleRoutes.length > 0;
    const isExplicitlyEmptyVehicles = Array.isArray(this.vehicleRoutes) && this.vehicleRoutes.length === 0;
    
    // Don't show no-data message in add-dialog mode (when vehicleRoutes is explicitly empty array)
    return hasMainRoute && !hasVehicleRoutes && !isExplicitlyEmptyVehicles;
  }

  private initializeMap() {
    if (!this.shouldShowMap() || this.map) {
      return;
    }

    try {
      // Create map with configurable interactions
      this.map = L.map(this.mapContainer.nativeElement, {
        zoomControl: this.interactive,
        dragging: this.interactive,
        touchZoom: this.interactive,
        scrollWheelZoom: this.interactive,
        doubleClickZoom: this.interactive,
        boxZoom: this.interactive,
        keyboard: this.interactive,
        attributionControl: true
      });

      // Add tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(this.map);

      this.updateMap();
    } catch (error) {
      // Map initialization failed - component will show no-data message
    }
  }

  private updateMap() {
    if (!this.map || !this.shouldShowMap()) {
      return;
    }

    // Clear existing markers, polylines and controls
    this.map.eachLayer((layer) => {
      if (layer instanceof L.Marker || layer instanceof L.Polyline) {
        this.map!.removeLayer(layer);
      }
    });

    // Remove existing reset view control if it exists
    if (this.resetViewControl && this.map) {
      this.map.removeControl(this.resetViewControl);
      this.resetViewControl = null;
    }

    const allMarkers: L.Marker[] = [];

    // Add main route if enabled and points exist
    if (this.showMainRoute && this.fromPoint && this.toPoint) {
      this.addMainRoute(allMarkers);
    }

    // Add vehicle routes
    if (this.vehicleRoutes && this.vehicleRoutes.length > 0) {
      this.addVehicleRoutes(allMarkers);
    }

    // Fit map to show all markers
    if (allMarkers.length > 0) {
      const group = L.featureGroup(allMarkers);
      this.initialBounds = group.getBounds();
      this.map.fitBounds(this.initialBounds, {
        padding: [20, 20]
      });
      
      // Add reset view button if interactive
      if (this.interactive) {
        this.addResetViewButton();
      }
    }
  }

  private addMainRoute(allMarkers: L.Marker[]) {
    if (!this.fromPoint || !this.toPoint || !this.map) return;

    const fromLatLng = L.latLng(this.fromPoint.lat, this.fromPoint.lon);
    const toLatLng = L.latLng(this.toPoint.lat, this.toPoint.lon);

    // Create custom icons for main route
    const startIcon = this.createMainRouteIcon('#22c55e');
    const endIcon = this.createMainRouteIcon('#ef4444');

    // Add markers
    const startMarker = L.marker(fromLatLng, { icon: startIcon })
      .bindTooltip(this.fromPoint.name, { permanent: false, direction: 'top' })
      .addTo(this.map);

    const endMarker = L.marker(toLatLng, { icon: endIcon })
      .bindTooltip(this.toPoint.name, { permanent: false, direction: 'top' })
      .addTo(this.map);

    allMarkers.push(startMarker, endMarker);

    // Add route line
    L.polyline([fromLatLng, toLatLng], {
      color: '#3b82f6',
      weight: 3,
      opacity: 0.8,
      dashArray: '5, 5' // Dashed line to distinguish from vehicle routes
    }).addTo(this.map);
  }

  private addVehicleRoutes(allMarkers: L.Marker[]) {
    if (!this.map) return;

    this.vehicleRoutes.forEach((vehicleRoute, index) => {
      const color = vehicleRoute.color || this.vehicleColors[index % this.vehicleColors.length];
      
      const fromLatLng = L.latLng(vehicleRoute.fromPoint.lat, vehicleRoute.fromPoint.lon);
      const toLatLng = L.latLng(vehicleRoute.toPoint.lat, vehicleRoute.toPoint.lon);

      // Create vehicle-specific icons
      const vehicleIcon = this.createVehicleIcon(vehicleRoute.type, color);

      // Add start marker for vehicle
      const startMarker = L.marker(fromLatLng, { icon: vehicleIcon })
        .bindTooltip(`${vehicleRoute.name} (${vehicleRoute.identifier}) - Start: ${vehicleRoute.fromPoint.name}`, 
                    { permanent: false, direction: 'top' })
        .addTo(this.map!);

      // Add end marker for vehicle
      const endMarker = L.marker(toLatLng, { icon: vehicleIcon })
        .bindTooltip(`${vehicleRoute.name} (${vehicleRoute.identifier}) - End: ${vehicleRoute.toPoint.name}`, 
                    { permanent: false, direction: 'bottom' })
        .addTo(this.map!);

      allMarkers.push(startMarker, endMarker);

      // Add vehicle route line with distance tooltip
      const distanceText = vehicleRoute.distance !== null && vehicleRoute.distance !== undefined
        ? `Dystans: ${vehicleRoute.distance.toFixed(2)} km`
        : 'Dystans: nie obliczono';
      
      // Use detailed coordinates if available, otherwise draw straight line
      let routeLatLngs: L.LatLng[];
      if (vehicleRoute.coordinates && vehicleRoute.coordinates.length > 0) {
        // Use the provided detailed coordinates for the actual route path
        routeLatLngs = vehicleRoute.coordinates;
      } else {
        // Fallback to straight line between start and end points
        routeLatLngs = [fromLatLng, toLatLng];
      }

      const polyline = L.polyline(routeLatLngs, {
        color: color,
        weight: 4,
        opacity: 0.9
      })
      .bindTooltip(`${vehicleRoute.name} (${vehicleRoute.identifier})<br/>${distanceText}`, {
        permanent: false,
        direction: 'center',
        className: 'vehicle-route-tooltip'
      })
      .addTo(this.map!);
    });
  }

  private createMainRouteIcon(color: string): L.DivIcon {
    return L.divIcon({
      html: `<div style="background-color: ${color}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
      iconSize: [16, 16],
      iconAnchor: [8, 8],
      className: 'custom-marker'
    });
  }

  private createVehicleIcon(vehicleType: string, color: string): L.DivIcon {
    const iconSymbol = this.getVehicleIconSymbol(vehicleType);
    
    return L.divIcon({
      html: `<div style="background-color: ${color}; color: white; width: 20px; height: 20px; border-radius: 3px; border: 2px solid #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: bold;">${iconSymbol}</div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      className: 'vehicle-marker'
    });
  }

  private getVehicleIconSymbol(vehicleType: string): string {
    const typeMap: { [key: string]: string } = {
      'truck': '🚛',
      'van': '🚐', 
      'car': '🚗',
      'bike': '🚲',
      'ship': '🚢',
      'plane': '✈️',
      'train': '🚂'
    };

    // Try to match vehicle type (case insensitive)
    const lowerType = vehicleType.toLowerCase();
    for (const [key, symbol] of Object.entries(typeMap)) {
      if (lowerType.includes(key)) {
        return symbol;
      }
    }

    // Default vehicle symbol
    return '🚛';
  }

  private destroyMap() {
    if (this.map) {
      // Remove reset view control if it exists
      if (this.resetViewControl) {
        this.map.removeControl(this.resetViewControl);
        this.resetViewControl = null;
      }
      
      this.map.remove();
      this.map = null;
      this.initialBounds = null;
    }
  }

  private addResetViewButton() {
    if (!this.map || !this.initialBounds || this.resetViewControl) return;

    // Create custom control for reset view button
    const ResetViewControl = L.Control.extend({
      onAdd: (map: L.Map) => {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom');
        container.style.backgroundColor = 'white';
        container.style.backgroundImage = 'none';
        container.style.width = '30px';
        container.style.height = '30px';
        container.style.cursor = 'pointer';
        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.justifyContent = 'center';
        container.innerHTML = '🎯';
        container.title = 'Reset widoku';

        container.onclick = () => {
          if (this.initialBounds && this.map) {
            this.map.fitBounds(this.initialBounds, {
              padding: [20, 20]
            });
          }
        };

        // Prevent map events when clicking the button
        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        return container;
      }
    });

    // Add the control to the map and store reference
    this.resetViewControl = new ResetViewControl({ position: 'topright' });
    this.resetViewControl.addTo(this.map);
  }
}
